import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    useTheme,
    alpha,
    Alert,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Star as StarIcon
} from '@mui/icons-material';
import { billingApi } from '../services/api';
import { useLocation } from 'react-router-dom';

const Pricing = () => {
    const theme = useTheme();
    const location = useLocation();
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [currency, setCurrency] = useState('usd');

    // Check URL parameters for success or canceled from Stripe redirect
    const queryParams = new URLSearchParams(location.search);
    const isSuccess = queryParams.get('success') === 'true';
    const isCanceled = queryParams.get('canceled') === 'true';

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const data = await billingApi.getConfig();
            setConfig(data);
        } catch (err) {
            console.error('Error fetching billing config:', err);
            setError('Could not load pricing plans. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubscribe = async (planId) => {
        setCheckoutLoading(planId);
        setError('');
        try {
            const { url } = await billingApi.createCheckoutSession(planId, currency);
            if (url) {
                window.location.href = url; // Redirect to Stripe Checkout
            }
        } catch (err) {
            console.error('Error creating checkout session:', err);
            setError('Failed to initiate checkout. Please try again.');
        } finally {
            setCheckoutLoading(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const plans = config?.plans || {};

    return (
        <Box sx={{ maxWidth: 1000, mx: 'auto', py: 4, px: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, px: 2, py: 1, borderRadius: 8, mb: 2 }}>
                    <StarIcon sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Upgrade Your Empire</Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>Simple, transparent pricing</Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                    Unlock the full power of AI for your social media strategy.
                </Typography>

                <ToggleButtonGroup
                    value={currency}
                    exclusive
                    onChange={(e, newCurrency) => {
                        if (newCurrency !== null) {
                            setCurrency(newCurrency);
                        }
                    }}
                    aria-label="currency"
                    size="small"
                >
                    <ToggleButton value="usd" aria-label="usd" sx={{ px: 3, fontWeight: 700 }}>
                        USD ($)
                    </ToggleButton>
                    <ToggleButton value="inr" aria-label="inr" sx={{ px: 3, fontWeight: 700 }}>
                        INR (₹)
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {isSuccess && (
                <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
                    Subscription successful! Your account has been upgraded. Please wait a few moments for the new claims to reflect.
                </Alert>
            )}

            {isCanceled && (
                <Alert severity="warning" sx={{ mb: 4, borderRadius: 2 }}>
                    Checkout was canceled. No charges were made.
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={4} justifyContent="center">
                {Object.entries(plans).map(([planId, planDetails]) => (
                    <Grid xs={12} md={5} key={planId}>
                        <Card sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-4px)' }
                        }}>
                            {planDetails.name === 'Pro' && (
                                <Chip
                                    label="MOST POPULAR"
                                    color="primary"
                                    size="small"
                                    sx={{ position: 'absolute', top: 16, right: 16, fontWeight: 700 }}
                                />
                            )}
                            <CardContent sx={{ flexGrow: 1, p: 4 }}>
                                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>{planDetails.name}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3 }}>
                                    <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                        {currency === 'inr' ? '₹' : '$'}
                                        {currency === 'inr' ? planDetails.price?.inr?.replace('₹', '') : planDetails.price?.usd?.replace('$', '')}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ ml: 1 }}>/ month</Typography>
                                </Box>

                                <Button
                                    fullWidth
                                    variant={planDetails.name === 'Creator Pro' ? 'contained' : 'outlined'}
                                    size="large"
                                    onClick={() => handleSubscribe(planId)}
                                    disabled={checkoutLoading === planId}
                                    sx={{ mb: 4, py: 1.5 }}
                                >
                                    {checkoutLoading === planId ? <CircularProgress size={24} /> : 'Subscribe Now'}
                                </Button>

                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', color: 'text.secondary' }}>
                                    What's included:
                                </Typography>
                                <List dense sx={{ mb: 0 }}>
                                    {(currency === 'inr' ? (planDetails.features_ind || []) : (planDetails.features_global || [])).map((feature, idx) => (
                                        <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <CheckCircleIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={feature} />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default Pricing;
